import React, { useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { Card, CardBody, CardTitle } from "reactstrap";
import classnames from "classnames";

import {
  fetchEventDetails,
  fetchEventDocument,
  fetchEventsById,
  fetchEventScores,
  fetchEventViews,
} from "../../../store/actions/events.actions";
import Loader from "../../../common/components/Loader";
import ErrorOverlay from "../../../common/components/ErrorOverlay";

import { useTypedSelector } from "../../../store/models/root.interface";
import ValueInTheRoom from "../../../components/Events/ValueInTheRoom";
import PresentersList, {
  PresenterListData,
} from "../../../components/Events/PresentersList";
import ScoresList, { ScoresListData } from "../../../components/Events/ScoresList";
import AttendeeStatistics from "../../../components/Events/AttendeeStatistics";
import ErrorBoundary from "../../../common/helpers/ErrorBoundary";

interface EventInfoSidebarProps {
  eventId: string;
  ticketId: string;
}

const EventInfoSidebar = ({ eventId, ticketId }: EventInfoSidebarProps) => {

  const dispatch = useDispatch();

  const eventsByUId = useTypedSelector(state => state.event.eventsByUid);
  const sessionsById = useTypedSelector(state => state.event.sessionsById);
  const presentersById = useTypedSelector(state => state.event.presentersById);
  const sessionsByEventId = useTypedSelector(state => state.event.sessionsByEventId);
  const presentersBySessionId = useTypedSelector(state => state.event.presentersBySessionId);
  const partnerAliasByEventUId = useTypedSelector(state => state.event.partnerAliasByEventUid);
  const scoresByEventUId = useTypedSelector(state => state.event.scoresByEventUid);
  const viewsByEventUid = useTypedSelector(state => state.event.viewsByEventUid);
  const downloads = useTypedSelector(state => state.event.downloadUrlByEventUid);

  const eventUid = useMemo(() => `${eventId}-${ticketId}`, [eventId, ticketId]);
  const event = useMemo(() => eventUid ? eventsByUId[eventUid] : void 0, [eventUid, eventsByUId]);

  const downloadUrl = useMemo(() => {
    const url = downloads[eventUid];
    if (typeof url === "string" && url !== "loading" && url !== "error") {
      return url;
    }
  }, [downloads, eventUid]);

  const fetchData = (eventId: string, ticketId: string) => {
    dispatch(fetchEventsById([eventId]));
    dispatch(fetchEventDetails(eventId));
    dispatch(fetchEventDocument(eventId, ticketId));
    dispatch(fetchEventViews(eventId, ticketId));
    dispatch(fetchEventScores(eventId, ticketId));
  };

  useEffect(() => {
    fetchData(eventId, ticketId);
  }, [eventId, ticketId]);

  const showViewsOfRoom = useMemo(() => {
    const views = viewsByEventUid[eventUid];
    if (views && (typeof views === "string" || views.length !== 0)) {
      return true;
    }
    return false;
  }, [eventUid, viewsByEventUid]);

  const viewsOfRoom = useMemo(() => {
    const data = viewsByEventUid[eventUid];
    if (data && typeof data !== "string") {
      return data;
    }
    return [];
  }, [eventUid, viewsByEventUid]);

  const showPresenters = useMemo(() => {
    const data = sessionsByEventId[eventId];
    if (data && (typeof data === "string" || data.length)) {
      return true;
    }
    return false;
  }, [eventId, sessionsByEventId]);

  const presenters = useMemo<PresenterListData[]>(() => {
    const sessions = sessionsByEventId[eventId];
    if (sessions && typeof sessions !== "string" && sessions.length) {
      return sessions
        .filter(sessionId => (presentersBySessionId[sessionId] ?? []).length !== 0)
        .map(sessionId => ({
          session: sessionsById[sessionId],
          presenters: (presentersBySessionId[sessionId] ?? [])
            .map(presenterId => presentersById[presenterId])
        }));
    }
    return [];
  }, [eventId, sessionsById, sessionsByEventId, presentersById, presentersBySessionId]);

  const showScores = useMemo(
    () => !!scoresByEventUId[eventUid],
    [eventUid, scoresByEventUId]);

  const scores = useMemo<ScoresListData[]>(() => {
    const scores = scoresByEventUId[eventUid];
    if (scores && typeof scores !== "string") {
      return Object.entries(scores).map(([company, scores]) => ({
        company,
        scores,
      }));
    }
    return [];
  }, [eventUid, scoresByEventUId]);

  const showAttendance = useMemo(
    () => !!eventsByUId[eventUid]?.attendance,
    [eventUid, eventsByUId]);

  const attendance = useMemo(
    () => eventsByUId[eventUid]?.attendance,
    [eventUid, eventsByUId]);

  return (
    <div className="sidebarWrapper">
      {eventId &&
        <>
          <div className="m-3">
            <Card>
              <CardTitle
                tag="h5"
                className="mb-0 py-3 text-primary text-center text-uppercase fs-9 fw-6"
              >
                {event?.isRound ? "Round" : "Event"}
              </CardTitle>
              <CardBody className="pt-0 text-left">
                <div className="sidebarWrapper__title">{event?.name}</div>

                {(event?.attendance?.total || downloadUrl) && (
                  <nav className="nav-menu mt-3">
                    <ul className="mainmenu">
                      {!!event?.attendance?.total && (
                        <li>
                          <Link
                            to={{
                              pathname: `/events/${eventId}/${ticketId}/attendees`,
                            }}
                          >
                            View attendees
                          </Link>
                        </li>
                      )}
                      {!!downloadUrl && (
                        <li>
                          <Link
                            target="_blank"
                            to={{
                              pathname: downloadUrl,
                            }}
                          >
                            Download information
                          </Link>
                        </li>
                      )}
                    </ul>
                  </nav>
                )}
              </CardBody>
            </Card>
          </div>
          {/* Hide VotR rather than unmounting in order to preserve its state */}
          <div className={classnames({ "d-none": !showViewsOfRoom })}>
            <div className="m-3">
              <ErrorBoundary>
                <div className="position-relative">
                  <ValueInTheRoom data={viewsOfRoom} />
                  <Loader show={viewsByEventUid[eventUid] === "loading"} />
                  <ErrorOverlay show={viewsByEventUid[eventUid] === "error"} />
                </div>
              </ErrorBoundary>
            </div>
          </div>
          {showScores && (
            <div className="m-3">
              <ErrorBoundary>
                <div className="position-relative">
                  <ScoresList data={scores} partnerName={partnerAliasByEventUId[eventUid]} />
                  <Loader show={scoresByEventUId[eventUid] === "loading"} />
                  <ErrorOverlay show={scoresByEventUId[eventUid] === "error"} />
                </div>
              </ErrorBoundary>
            </div>
          )}
          {showPresenters && (
            <div className="m-3">
              <ErrorBoundary>
                <div className="position-relative">
                  <PresentersList data={presenters} />
                  <Loader show={sessionsByEventId[eventId] === "loading"} />
                  <ErrorOverlay show={sessionsByEventId[eventId] === "error"} />
                </div>
              </ErrorBoundary>
            </div>
          )}
          {showAttendance && (
            <div className="m-3">
              <ErrorBoundary>
                <div className="position-relative">
                  <AttendeeStatistics data={attendance} />
                </div>
              </ErrorBoundary>
            </div>
          )}
        </>
      }
    </div>
  );
};

export default EventInfoSidebar;
